import type { RowErrorReason } from '../components/ui/RowError';
import { fmtCurrency, sumMoney } from './display-labels';

/**
 * TICKET DISCOUNT LINES — one helper for both editors.
 *
 * The office editor (NewTicketScreen) and the field editor
 * (screens/field/FieldTicketsScreen) both offer "Add discount". Everything
 * about what a discount line IS lives here, so the two cannot drift: the label,
 * the helper text, what is sent, what is read back, and what is checked before
 * a save.
 *
 * ─── The data model (API, feat/ticket-discounts) ───────────────────────────
 *
 * A discount is an ordinary ticket line (`line_item_type` 'other') plus
 * `discount_kind` and `discount_percent` / `discount_amount`. There is no
 * separate store and no separate endpoint; the line rides the ticket save.
 *
 *   MULTI RIG  percent only, one per ticket. THE SERVER OWNS THE AMOUNT: it
 *              sets the total to -round(percent x the non-discount lines, 2)
 *              and recomputes it on every line write. Reduces hands' pay.
 *   ISSUE      a name and either a $ amount or a %. A % becomes a SET total
 *              on the save it is written with and is never recomputed after.
 *              Hands' pay unchanged.
 *
 * ─── Why the editor shows an amount it did not get from the server ────────
 *
 * Non-negotiable 5 says never recompute a figure the API returns. A discount
 * line's amount IS one — so the server's total is what renders, always, once
 * the server has said something about the lines on screen. The editor shows its
 * own figure only for lines the server has NOT priced yet (a line just added,
 * or a Multi Rig line whose base lines the hand has changed since the ticket
 * was read), and that figure is labelled an estimate in words on the row. It is
 * never sent as the total: the server ignores a Multi Rig total and works an
 * Issue % out for itself.
 *
 * The estimate is the only way the brief's "amount updates when lines change"
 * can hold offline, where there is no server to ask.
 */

export type DiscountKind = 'multi_rig' | 'issue';
export type IssueMode = 'amount' | 'percent';

export const DISCOUNT_KINDS: readonly DiscountKind[] = ['multi_rig', 'issue'];

/** The Multi Rig rate offered when the line is added. The hand may change it. */
export const MULTI_RIG_DEFAULT_PERCENT = 5;

export const DISCOUNT_KIND_LABEL: Record<DiscountKind, string> = {
  multi_rig: 'Multi Rig',
  issue: 'Issue',
};

/** Pay wording. Editor-only — never passed to anything a customer reads. */
export const MULTI_RIG_HELPER = "Reduces hands' pay.";
export const ISSUE_HELPER = "Hands' pay unchanged.";
export const ISSUE_PERCENT_NOTE = "Won't update if lines change.";
export const DISCOUNT_ESTIMATE_NOTE = 'Estimate until saved.';
export const MULTI_RIG_ALREADY_ON_TICKET = 'This ticket already has a Multi Rig discount.';

export const DISCOUNT_PERCENT_REQUIRED = 'Enter a percent. Use 0 for no discount.';
export const DISCOUNT_AMOUNT_REQUIRED = 'Enter an amount. Use 0 for no discount.';
export const DISCOUNT_NAME_REQUIRED = 'Name the issue this discount is for.';

/** The fields a discount line carries. A plain line has none of them set. */
export interface DiscountFields {
  discountKind?: DiscountKind | null;
  /** Multi Rig's percent, or an Issue line's percent (typed or recorded). */
  discountPercent?: number | null;
  /** An Issue line's typed $ amount, POSITIVE. */
  discountAmount?: number | null;
  /** Client only: which box an Issue line was entered in. */
  discountIssueMode?: IssueMode;
  /** Client only: the issue name, which the description is built from. */
  discountName?: string;
  /**
   * Client only: the signature of the base lines the stored total was priced
   * from. A Multi Rig line whose signature still matches renders the server's
   * total; one that does not renders an estimate. Absent = never priced by the
   * server (a line added on this screen).
   */
  discountBaseSig?: string;
  /**
   * Client only: an Issue % the hand set or changed on this screen, so the
   * server must work it out on this save. Otherwise a saved Issue line is sent
   * by its stored amount and never recomputed — see `discountWireFields`.
   */
  discountPercentDirty?: boolean;
}

/** The minimum of a line this file reads. Both editors' LineItem satisfy it. */
export interface DiscountableLine extends DiscountFields {
  id?: string | null;
  lineItemType: string;
  description: string;
  unit: string | null;
  rate: number | null;
  quantity: number | null;
  total: number | null;
  assignedTech1Initials: string | null;
  assignedTech2Initials: string | null;
  assignedTech3Initials: string | null;
  assignedTech4Initials: string | null;
  assignedTech1Id: string | null;
  assignedTech2Id: string | null;
  assignedTech3Id: string | null;
  assignedTech4Id: string | null;
}

/** What the "Add discount" control hands back. */
export interface DiscountSpec {
  kind: DiscountKind;
  name?: string;
  mode?: IssueMode;
  percent?: number | null;
  amount?: number | null;
}

export function isDiscountKind(v: unknown): v is DiscountKind {
  return v === 'multi_rig' || v === 'issue';
}

export function isDiscountLine(li: DiscountFields | null | undefined): boolean {
  return !!li && isDiscountKind(li.discountKind);
}

export function hasMultiRig(lines: readonly DiscountFields[], exceptIndex?: number): boolean {
  return lines.some((li, i) => i !== exceptIndex && li.discountKind === 'multi_rig');
}

const PREFIX = 'Discount - ';

/** "5", "2.5" — no trailing zeros, because the label is prose, not money. */
function fmtPercent(p: number): string {
  return String(Math.round(p * 10000) / 10000);
}

/**
 * The line's description, which is also its label everywhere the line goes —
 * the PDF, the invoice and QuickBooks read the description, not the kind.
 *
 *   Multi Rig            "Discount - Multi Rig (5%)"
 *   Issue, $             "Discount - Late rig-up"
 *   Issue, %             "Discount - Late rig-up (5%)"
 *
 * A blank percent leaves the "(n%)" off rather than printing "(%)"; the row is
 * refused before save in that state anyway.
 */
export function discountDescription(spec: {
  kind: DiscountKind;
  name?: string | null;
  mode?: IssueMode;
  percent?: number | null;
}): string {
  const pct = spec.percent != null && Number.isFinite(spec.percent) ? ` (${fmtPercent(spec.percent)}%)` : '';
  if (spec.kind === 'multi_rig') return `${PREFIX}Multi Rig${pct}`;
  const name = (spec.name ?? '').trim();
  return `${PREFIX}${name}${spec.mode === 'percent' ? pct : ''}`;
}

/** Read an Issue line's name back out of its description (the inverse of the above). */
export function issueNameFromDescription(description: string | null | undefined): string {
  let s = (description ?? '').trim();
  if (s.startsWith(PREFIX)) s = s.slice(PREFIX.length);
  return s.replace(/\s*\(\s*-?[\d.]+\s*%\s*\)\s*$/, '').trim();
}

/**
 * Which totals a discount is a percent OF: every line that is not itself a
 * discount. All or nothing, like the editor subtotal — one unpriced line makes
 * the base unknown, and an estimate over a partial base would be a confident
 * wrong number.
 */
export function discountBase(lines: readonly DiscountableLine[]): number | null {
  return sumMoney(lines.filter(li => !isDiscountLine(li)).map(li => li.total));
}

/**
 * Identity of the base lines' totals, in order. Two reads of the same lines
 * give the same string; any change to a base line's total, or a base line
 * added or removed, gives a different one.
 */
export function baseSignature(lines: readonly DiscountableLine[]): string {
  return JSON.stringify(lines.filter(li => !isDiscountLine(li)).map(li => li.total ?? null));
}

/** -round(base x percent / 100, 2), or null when either is unknown. Never -0. */
export function percentDiscountTotal(base: number | null, percent: number | null | undefined): number | null {
  if (base == null || percent == null || !Number.isFinite(base) || !Number.isFinite(percent)) return null;
  // The server prices nothing off a base at or below zero; the estimate agrees.
  if (base <= 0) return 0;
  const v = -Math.round(base * percent) / 100;
  return v === 0 ? 0 : v;
}

/** A typed positive amount as the line's negative total. Never -0. */
function amountDiscountTotal(amount: number | null | undefined): number | null {
  if (amount == null || !Number.isFinite(amount)) return null;
  // The server stores -|amount|, so a typed "-50" is a $50 discount here too.
  return amount === 0 ? 0 : -Math.abs(amount);
}

/** Rate, quantity and total for a discount line with the given total. */
function priced(total: number | null): Pick<DiscountableLine, 'rate' | 'quantity' | 'total'> {
  return { rate: total, quantity: 1, total };
}

const NO_CREW = {
  assignedTech1Initials: null,
  assignedTech2Initials: null,
  assignedTech3Initials: null,
  assignedTech4Initials: null,
  assignedTech1Id: null,
  assignedTech2Id: null,
  assignedTech3Id: null,
  assignedTech4Id: null,
} as const;

/**
 * A new discount line from the "Add discount" control, laid over the editor's
 * own blank row (so each editor keeps whatever else its rows carry).
 *
 * No crew: a discount is not work anybody did. An Issue % is priced ONCE, here,
 * from the lines on screen — that is "won't update if lines change" — and the
 * server prices it again on the save it is written with.
 */
export function buildDiscountLine<T extends DiscountableLine>(
  blank: T,
  spec: DiscountSpec,
  lines: readonly DiscountableLine[],
): T {
  const mode: IssueMode | undefined = spec.kind === 'issue' ? (spec.mode ?? 'amount') : undefined;
  const percent = spec.kind === 'multi_rig' || mode === 'percent' ? (spec.percent ?? null) : null;
  const amount = mode === 'amount' ? (spec.amount ?? null) : null;
  const total =
    mode === 'amount' ? amountDiscountTotal(amount) : percentDiscountTotal(discountBase(lines), percent);
  return {
    ...blank,
    ...NO_CREW,
    id: blank.id ?? null,
    lineItemType: 'other',
    unit: null,
    description: discountDescription({ kind: spec.kind, name: spec.name, mode, percent }),
    ...priced(total),
    discountKind: spec.kind,
    discountPercent: percent,
    discountAmount: amount,
    discountIssueMode: mode,
    discountName: spec.kind === 'issue' ? (spec.name ?? '') : undefined,
    discountBaseSig: undefined,
    discountPercentDirty: spec.kind === 'issue' && mode === 'percent' ? true : undefined,
  };
}

/** A change made on the discount row itself. */
export type DiscountEdit =
  | { field: 'percent'; value: number | null }
  | { field: 'amount'; value: number | null }
  | { field: 'name'; value: string }
  | { field: 'mode'; value: IssueMode };

/**
 * Apply an edit to a discount line. The description is rebuilt from the parts,
 * so the label always says what the line is. An Issue % is re-priced from the
 * lines on screen at the moment of the edit — and only then.
 */
export function editDiscountLine<T extends DiscountableLine>(
  line: T,
  edit: DiscountEdit,
  lines: readonly DiscountableLine[],
): T {
  if (!isDiscountLine(line)) return line;
  const kind = line.discountKind as DiscountKind;
  let { discountPercent: percent = null, discountAmount: amount = null } = line;
  let name = line.discountName ?? '';
  let mode = line.discountIssueMode;
  let dirty = line.discountPercentDirty;
  let total = line.total;

  if (edit.field === 'name') name = edit.value;
  if (edit.field === 'percent') percent = edit.value;
  if (edit.field === 'amount') amount = edit.value;
  if (edit.field === 'mode') mode = edit.value;

  if (kind === 'issue') {
    if (edit.field === 'amount' || (edit.field === 'mode' && mode === 'amount')) {
      percent = null;
      dirty = undefined;
      total = amountDiscountTotal(amount);
    } else if (edit.field === 'percent' || (edit.field === 'mode' && mode === 'percent')) {
      amount = null;
      dirty = true;
      total = percentDiscountTotal(discountBase(lines), percent);
    }
  }

  return {
    ...line,
    description: discountDescription({ kind, name, mode, percent }),
    ...(kind === 'issue' ? priced(total) : {}),
    discountPercent: percent,
    discountAmount: amount,
    discountIssueMode: mode,
    discountName: kind === 'issue' ? name : undefined,
    discountPercentDirty: dirty,
    // A Multi Rig percent the hand changed is not the one the server priced.
    discountBaseSig: kind === 'multi_rig' && edit.field === 'percent' ? undefined : line.discountBaseSig,
  };
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * The discount fields of a server row (either casing — camelCase on the office
 * read, snake_case under /employee/*). `{}` for a plain line, so spreading this
 * into a hydrated row changes nothing about lines that are not discounts.
 */
export function readDiscountFields(raw: any): DiscountFields {
  const kind = raw?.discountKind ?? raw?.discount_kind;
  if (!isDiscountKind(kind)) return {};
  const percent = num(raw.discountPercent ?? raw.discount_percent);
  const amount = num(raw.discountAmount ?? raw.discount_amount);
  if (kind === 'multi_rig') return { discountKind: kind, discountPercent: percent, discountAmount: null };
  const total = num(raw.total);
  const mode: IssueMode = percent != null ? 'percent' : 'amount';
  return {
    discountKind: kind,
    discountPercent: percent,
    /*
     * THE API STORES NO AMOUNT COLUMN — only the line's total. An Issue $ line
     * therefore comes back with no discount_amount at all, and reading that as
     * "no amount" would blank the box and refuse every later save of the
     * ticket. The amount it was entered with IS |total|. `amount` from the
     * payload is only ever present on this device's own unsynced copy.
     */
    discountAmount: amount ?? (mode === 'amount' && total != null ? Math.abs(total) : null),
    discountIssueMode: mode,
    discountName: issueNameFromDescription(raw.description),
    /*
     * A % line with no total has not been priced by the server yet — the
     * device's own copy of an offline save, which carries only the percent.
     * It must go out as a percent again, never as an amount made from an
     * estimate, or the server's own pricing is overwritten by ours.
     */
    discountPercentDirty: mode === 'percent' && total == null ? true : undefined,
  };
}

/**
 * Stamp hydrated rows with the base they were priced from. Call on EVERY read
 * of lines from the server (open, and the save response): it is what makes the
 * server's Multi Rig total the one on screen until a base line changes.
 */
export function stampDiscountBase<T extends DiscountableLine>(lines: T[]): T[] {
  if (!lines.some(li => li.discountKind === 'multi_rig')) return lines;
  const sig = baseSignature(lines);
  // Only a line the server actually priced. A Multi Rig line with no total is
  // this device's copy of an unsynced save (it sends no total), and must keep
  // showing the estimate rather than an em dash.
  return lines.map(li => (li.discountKind === 'multi_rig' && li.total != null ? { ...li, discountBaseSig: sig } : li));
}

/**
 * Is this discount line's amount the editor's estimate rather than the
 * server's figure? Drives the "Estimate until saved." note.
 */
export function isDiscountEstimate(line: DiscountableLine, lines: readonly DiscountableLine[]): boolean {
  if (line.discountKind === 'multi_rig') return line.discountBaseSig !== baseSignature(lines);
  if (line.discountKind === 'issue') return !!line.discountPercentDirty;
  return false;
}

/**
 * The lines as they should be SHOWN and summed: a Multi Rig line whose base has
 * changed since the server priced it carries a fresh estimate; every other line
 * is returned untouched (same object), including a Multi Rig line the server
 * has priced against exactly these lines.
 */
export function withDiscountEstimates<T extends DiscountableLine>(lines: T[]): T[] {
  const needs = (li: T) =>
    li.discountKind === 'multi_rig' || (li.discountKind === 'issue' && li.discountPercentDirty && li.total == null);
  if (!lines.some(needs)) return lines;
  const sig = baseSignature(lines);
  const base = discountBase(lines);
  return lines.map(li => {
    if (!needs(li) || (li.discountKind === 'multi_rig' && li.discountBaseSig === sig)) return li;
    return { ...li, ...priced(percentDiscountTotal(base, li.discountPercent)) };
  });
}

/**
 * The lines with every ESTIMATED discount amount taken out (total and rate
 * absent), for anything that is not the editor row itself — the review step the
 * customer reads, and the preview. Those surfaces carry no "estimate" label, and
 * their totals are the server's, so an unlabelled client figure there would be
 * the second answer non-negotiable 5 forbids. Absent renders "—".
 */
export function withoutDiscountEstimates<T extends DiscountableLine>(lines: T[]): T[] {
  if (!lines.some(isDiscountLine)) return lines;
  return lines.map(li => (isDiscountLine(li) && isDiscountEstimate(li, lines) ? { ...li, rate: null, total: null } : li));
}

/**
 * The discount keys this line puts on the wire, snake_case as the API reads
 * them on every ticket door. `{}` for a plain line.
 *
 *   Multi Rig       kind + percent. Amount is the server's; nothing else sent.
 *   Issue, $        kind + amount.
 *   Issue, % new/edited    kind + percent only — the server prices it on this save.
 *   Issue, % saved, untouched   kind + amount (|stored total|) + percent (as a record),
 *                    because a replace-all save re-inserts the row, and a bare
 *                    percent would be re-priced from today's lines.
 */
export function discountWireFields(line: DiscountableLine): Record<string, unknown> {
  if (line.discountKind === 'multi_rig') {
    return { discount_kind: 'multi_rig', discount_percent: line.discountPercent ?? null };
  }
  if (line.discountKind !== 'issue') return {};
  if (line.discountIssueMode === 'percent') {
    if (line.discountPercentDirty || line.total == null) {
      return { discount_kind: 'issue', discount_percent: line.discountPercent ?? null };
    }
    return {
      discount_kind: 'issue',
      discount_amount: Math.abs(line.total),
      discount_percent: line.discountPercent ?? null,
    };
  }
  return { discount_kind: 'issue', discount_amount: line.discountAmount ?? null };
}

const CLIENT_ONLY_KEYS = [
  'discountKind',
  'discountPercent',
  'discountAmount',
  'discountIssueMode',
  'discountName',
  'discountBaseSig',
  'discountPercentDirty',
] as const;

/**
 * A line as it goes on the wire: the editor's row minus the client-only discount
 * keys, plus `discountWireFields`, with every crew slot cleared on a discount.
 *
 * EVERY PLAIN LINE CARRIES `discount_kind: null`. That key is how the API tells
 * a discount-aware payload from an older bundle's: an unaware payload cannot
 * remove or change a stored discount line (the server keeps it), so without it
 * a hand who deleted a discount would save, see success, and find it back on
 * reopening. Otherwise a plain line goes out exactly as it was held.
 *
 * A Multi Rig line sends no rate, quantity, total, unit or type — the server
 * sets all of them, and the estimate on screen must never reach the column.
 */
export function lineForWire<T extends DiscountableLine>(line: T): T {
  const out: any = { ...line };
  for (const k of CLIENT_ONLY_KEYS) delete out[k];
  if (!isDiscountLine(line)) return { ...out, discount_kind: null };
  // Multi Rig, and an Issue % the server must price on this save, send no
  // money of their own. Besides being ignored, an estimate on the wire would be
  // written into this device's copy of an offline save and read back later as
  // if the server had priced it.
  if (line.discountKind === 'multi_rig' || discountWireFields(line).discount_amount === undefined) {
    for (const k of ['rate', 'quantity', 'total', 'unit', 'lineItemType']) delete out[k];
  }
  return { ...out, ...NO_CREW, ...discountWireFields(line) };
}

/** The fields the server marks a discount refusal with. */
export const DISCOUNT_ERROR_FIELDS = ['discount_kind', 'discount_percent', 'discount_amount'] as const;

/**
 * What a discount line owes before save. Blank is refused; a typed 0 is an
 * answer and passes. Range (below 0, 100 and over) is the server's call, and
 * its message lands on the row.
 */
export function validateDiscountLine(
  line: DiscountableLine,
  index: number,
  lines: readonly DiscountableLine[],
): RowErrorReason[] {
  const reasons: RowErrorReason[] = [];
  if (line.discountKind === 'multi_rig') {
    if (line.discountPercent == null) reasons.push({ message: DISCOUNT_PERCENT_REQUIRED, field: 'discount_percent' });
    if (hasMultiRig(lines, index)) {
      reasons.push({ message: MULTI_RIG_ALREADY_ON_TICKET, field: 'discount_kind' });
    }
  } else if (line.discountKind === 'issue') {
    if (!(line.discountName ?? '').trim()) reasons.push({ message: DISCOUNT_NAME_REQUIRED, field: 'description' });
    if (line.discountIssueMode === 'percent') {
      if (line.discountPercent == null) reasons.push({ message: DISCOUNT_PERCENT_REQUIRED, field: 'discount_percent' });
    } else if (line.discountAmount == null) {
      reasons.push({ message: DISCOUNT_AMOUNT_REQUIRED, field: 'discount_amount' });
    }
  }
  return reasons;
}

/**
 * A line amount as a reader expects money to look: a negative is "-$256.47",
 * never "$-256.47". Zero and positives are fmtCurrency exactly, so every line
 * that was not below zero renders as it always did.
 */
export function fmtLineMoney(v: number | string | null | undefined, currency?: string | null, digits = 2): string {
  const n = v === null || v === undefined || v === '' ? NaN : Number(v);
  if (Number.isFinite(n) && n < 0) {
    // A sub-cent negative rounds to nothing; "$-0.00" and "-$0.00" are both wrong.
    if (Math.round(n * 10 ** digits) === 0) return fmtCurrency(0, currency, digits);
    return `-${fmtCurrency(-n, currency, digits)}`;
  }
  return fmtCurrency(v, currency, digits);
}

/** Is this line below zero — the test the customer-facing documents label "Discount" by. */
export function isBelowZero(v: number | string | null | undefined): boolean {
  if (v === null || v === undefined || v === '') return false;
  const n = Number(v);
  return Number.isFinite(n) && n < 0;
}

/** Which refusal marks an edit to the discount row answers — field-scoped clearing. */
export function discountFieldsFor(edit: DiscountEdit): string[] {
  switch (edit.field) {
    case 'name': return ['description'];
    case 'percent': return ['discount_percent'];
    case 'amount': return ['discount_amount'];
    case 'mode': return ['discount_percent', 'discount_amount'];
    default: return [];
  }
}

/**
 * A REFUSAL THAT ARRIVED AFTER THE HAND WALKED AWAY, put back on the discount
 * line it is about.
 *
 * An offline save replays through /sync/push, and the server's reasons come
 * back keyed by position in THAT save's lines — not in whatever is on screen
 * when the ticket is reopened. A pull may have replaced the lines since, so a
 * reason is placed only where the queued line and the line now at the same
 * position are both discounts with the same description. Anything else is
 * dropped from the row and still reaches the reader through the red sync bar,
 * which shows the server's text. A mark on the wrong line would be worse than
 * none: it reads as a second fault.
 */
export function queuedDiscountRefusals(
  queuedLines: readonly any[],
  details: ReadonlyArray<{ index: number; field?: string; message: string }>,
  shown: readonly DiscountableLine[],
): Record<number, RowErrorReason[]> {
  const out: Record<number, RowErrorReason[]> = {};
  for (const d of details) {
    if (!Number.isInteger(d.index) || !d.message) continue;
    const queued = queuedLines[d.index];
    const here = shown[d.index];
    const queuedKind = queued?.discount_kind ?? queued?.discountKind;
    if (!isDiscountKind(queuedKind) || !here || here.discountKind !== queuedKind) continue;
    if ((queued.description ?? '') !== (here.description ?? '')) continue;
    // The values refused must still be the values on the line. A refused
    // amount the hand has since corrected (and saved) keeps its description,
    // so without this the old message would come back on every reopen.
    const now = discountWireFields(here);
    if (num(queued.discount_percent) !== num(now.discount_percent)) continue;
    if (queued.discount_amount !== undefined && num(queued.discount_amount) !== num(now.discount_amount)) continue;
    (out[d.index] ??= []).push({ message: d.message, field: d.field });
  }
  return out;
}
